import { useCallback, useEffect, useRef, useState } from 'react'

import { useControlsVisibility } from '../hooks/useControlsVisibility'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import ReactPlayer from 'react-player'
import { useDispatch } from 'react-redux'

import { Box } from '@chakra-ui/react'
import screenfull from 'screenfull'

import { setVideoPlaying } from '@/redux/ui/uiSlice'

import { VIDEO_ASPECT_RATIO_PADDING } from '../constants/dimensions'
import { VideoControlsAction } from '../types/types'
import {
  PlaybackDiagnosticsIProps,
  PlaybackFailureReason,
  getPlaybackDiagnostics,
} from '../utils/playbackDiagnostics'
import { ProgressBarOverlay } from './ProgressBarOverlay'
import { VideoControls } from './VideoControls'
import { VideoError } from './VideoError'
import { VideoProcessing } from './VideoProcessing'

// How long after a URL is set we wait for a first frame before treating the
// player as stalled (covers requests that hang rather than error, e.g. S3
// blocked by a corporate firewall).
const NO_FIRST_FRAME_TIMEOUT = 10_000

export interface IProps {
  url: string
  autoplay?: boolean
  // Override to hide controls
  showControls?: boolean
  pipActive?: boolean
  disablePIP?: () => void
  onToggleLive?: (boolean) => void
  // Only used in controls (i.e. overridden if controls = false)
  liveEnabled?: boolean
  // Used to set minWidth
  placeholderWidth?: string
  // Used to set minHeight
  placeholderHeight?: string
  // Only used in controls (i.e. overridden if controls = false)
  showToggleLiveButton?: boolean
  errorLabel?: string
  isProcessing?: boolean
  onPlayerControlAction?: (event: VideoControlsAction) => void
  onStartPlaying?: () => void
  // Fired when the <video> element raises an error after the URL was set.
  onPlaybackError?: (diagnostics: PlaybackDiagnosticsIProps) => void
  // Fired when the URL was set but no first frame rendered within the timeout.
  onPlaybackStalled?: (diagnostics: PlaybackDiagnosticsIProps) => void
  // Only used in controls (i.e. overridden if controls = false)
  liveOnly?: boolean
  // When true, clip button shows loading state in controls
  isClipLoading?: boolean
  // Label for clip button when not loading
  clipLabel?: string
  // Called when the user clicks the Clip toggle
  onClipClick?: () => void
  // Called when the user clicks the Extended toggle
  onExtendedClick?: () => void
  // When true, the Extended toggle is the active selection
  isExtendedActive?: boolean
  // When true, an EXTENDED clip is available and the Extended toggle should be shown
  hasExtendedClip?: boolean
  // When true, skip dispatching setVideoPlaying to Redux. Use for secondary
  // video contexts (e.g. grid testing) that should not affect the Incident
  // Drawer's polling behavior.
  suppressGlobalPlayState?: boolean
  borderRadius?: string
}

export const VideoPlayer = ({
  url,
  pipActive,
  disablePIP,
  onToggleLive,
  placeholderWidth,
  placeholderHeight,
  onPlayerControlAction,
  onStartPlaying,
  onPlaybackError,
  onPlaybackStalled,
  errorLabel,
  isProcessing,
  autoplay = true,
  liveEnabled = false,
  showToggleLiveButton = true,
  liveOnly = false,
  showControls = true,
  isClipLoading = false,
  clipLabel,
  onClipClick,
  onExtendedClick,
  isExtendedActive = false,
  hasExtendedClip = false,
  suppressGlobalPlayState = false,
  borderRadius = '0.375rem',
}: IProps) => {
  const dispatch = useDispatch()
  const isDownloadEnabled = useFeatureFlag(
    FeatureFlag.downloadableIncidentVideos
  )
  const [state, setState] = useState({
    isPlaying: autoplay,
    played: 0,
    playedSeconds: 0,
    seeking: false,
    isPip: false,
  })
  const [duration, setDuration] = useState(0)
  const [isLiveActive, setIsLiveActive] = useState(liveEnabled)
  const isPlayingRef = useRef(autoplay)

  const { isControlsVisible, handleMouseMove, handleMouseLeave, tickAutoHide } =
    useControlsVisibility()

  const playerRef = useRef(null)
  const playerContainerRef = useRef(null)
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasStartedRef = useRef(false)
  const { isPlaying, played, playedSeconds, isPip } = state

  const handlePlayPause = () => {
    const isNextPlaying = !isPlayingRef.current
    isPlayingRef.current = isNextPlaying
    setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }))
    onPlayerControlAction?.(
      isNextPlaying ? VideoControlsAction.Play : VideoControlsAction.Pause
    )
  }

  const handleRewind = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() - 10)
    onPlayerControlAction?.(VideoControlsAction.Backward10)
  }

  const handleFastForward = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() + 10)
    onPlayerControlAction?.(VideoControlsAction.Forward10)
  }

  const handleProgress = (changeState) => {
    tickAutoHide()
    setState((prev) => (prev.seeking ? prev : { ...prev, ...changeState }))
  }

  const handleSeekChange = (newValue) => {
    const value = newValue / 100
    setState((prev) => ({ ...prev, played: parseFloat(`${value}`) }))
    playerRef.current.seekTo(value, 'fraction')
  }

  const handleDuration = (dur: number) => {
    setDuration(dur)
  }

  const toggleFullScreen = () => {
    if (screenfull.isEnabled) screenfull.toggle(playerContainerRef.current)
    onPlayerControlAction?.(VideoControlsAction.Fullscreen)
  }

  const handleToggleLive = useCallback(
    (live: boolean) => {
      setIsLiveActive(live)
      onToggleLive?.(live)
    },
    [onToggleLive]
  )

  const handleEnablePIP = () => {
    setState((prev) => ({ ...prev, isPip: true }))
  }

  const handleDisablePIP = () => {
    disablePIP()
    setState((prev) => ({ ...prev, isPip: false }))
  }

  // react-player's getInternalPlayer() returns the underlying <video> element
  // for file (clip) URLs, which is where the MediaError and load state live.
  const getVideoElement = (): HTMLVideoElement | null => {
    const internal = playerRef.current?.getInternalPlayer?.()
    return internal instanceof HTMLVideoElement ? internal : null
  }

  const clearWatchdog = () => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }

  const handleMediaError = () => {
    clearWatchdog()
    onPlaybackError?.(
      getPlaybackDiagnostics(
        getVideoElement(),
        PlaybackFailureReason.MediaError,
        url
      )
    )
  }

  const handleStart = () => {
    hasStartedRef.current = true
    clearWatchdog()
    onStartPlaying?.()
  }

  // Watchdog for the "black player, no error, no spinner" case: the URL is set
  // but playback never produces a first frame (e.g. the media request hangs).
  useEffect(() => {
    hasStartedRef.current = false
    clearWatchdog()
    if (url && !isProcessing && !errorLabel) {
      watchdogRef.current = setTimeout(() => {
        if (!hasStartedRef.current) {
          onPlaybackStalled?.(
            getPlaybackDiagnostics(
              getVideoElement(),
              PlaybackFailureReason.NoFirstFrame,
              url,
              NO_FIRST_FRAME_TIMEOUT
            )
          )
        }
      }, NO_FIRST_FRAME_TIMEOUT)
    }
    return clearWatchdog
    // onPlaybackStalled is intentionally omitted: it is a fresh closure each
    // render and would otherwise restart the timer on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, isProcessing, errorLabel])

  useEffect(() => {
    setState((prev) => ({ ...prev, isPip: pipActive }))
  }, [pipActive])

  useEffect(() => {
    if (!suppressGlobalPlayState) {
      dispatch(setVideoPlaying(isPlaying))
    }
  }, [dispatch, isPlaying, suppressGlobalPlayState])

  useEffect(
    () => () => {
      if (!suppressGlobalPlayState) {
        dispatch(setVideoPlaying(false))
      }
    },
    [dispatch, suppressGlobalPlayState]
  )

  const shouldShowControls = showControls
  const shouldShowProgressBar = shouldShowControls && !liveOnly && !isLiveActive

  return (
    <Box
      bg='black'
      borderRadius={borderRadius}
      // Disable right-click context menu to prevent video download options when FF is false
      onContextMenu={isDownloadEnabled ? undefined : (e) => e.preventDefault()}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      overflow='hidden'
      position='relative'
      ref={playerContainerRef}
    >
      <Box
        minH={placeholderHeight}
        minW={placeholderWidth}
        pt={VIDEO_ASPECT_RATIO_PADDING}
      >
        {errorLabel && <VideoError errorLabel={errorLabel} />}
        {isProcessing && <VideoProcessing />}
        <ReactPlayer
          data-testid='video-player'
          height='100%'
          loop
          muted
          onDisablePIP={handleDisablePIP}
          onDuration={handleDuration}
          onEnablePIP={handleEnablePIP}
          onError={handleMediaError}
          onProgress={handleProgress}
          onStart={handleStart}
          pip={isPip}
          playing={isPlaying}
          ref={playerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          url={url}
          width='100%'
        />
        {shouldShowProgressBar && (
          <ProgressBarOverlay
            isControlsVisible={isControlsVisible}
            played={played}
          />
        )}
        {shouldShowControls && (
          <VideoControls
            clipLabel={clipLabel}
            currentTime={playedSeconds}
            duration={duration}
            hasExtendedClip={hasExtendedClip}
            isClipLoading={isClipLoading}
            isExtendedActive={isExtendedActive}
            isPlaying={isPlaying}
            isVisible={isControlsVisible}
            liveEnabled={liveEnabled}
            liveOnly={liveOnly}
            onClipClick={onClipClick}
            onExtendedClick={onExtendedClick}
            onFastForward={handleFastForward}
            onPlayPause={handlePlayPause}
            onRewind={handleRewind}
            onSeek={handleSeekChange}
            onToggleFullScreen={toggleFullScreen}
            onToggleLive={handleToggleLive}
            played={played}
            showToggleLiveButton={showToggleLiveButton}
          />
        )}
      </Box>
    </Box>
  )
}
